import {css, view} from '@sail/ui/deprecated';
import {MessageDescriptor} from 'react-intl';
import {Header} from './Header';
import {useWidgetVisibility} from './useWidget';
import {isIframed} from './iframe';
import {useUniversalHelpDotComDesign} from '../FeatureFlagsProvider';

type PaneProps = {
  children: React.ReactNode;
};

function Pane({children}: PaneProps) {
  const {visibility} = useWidgetVisibility();

  if (visibility === 'closed') {
    return null;
  }

  const styles = [
    css({
      position: css.value('relative'),
      flexGrow: css.value('1'),
      stack: 'y',
      overflow: 'hidden',
    }),
  ];

  if (visibility !== 'open') {
    styles.push(
      css({
        height: css.value('0'),
        visibility: css.value('hidden'),
      }),
    );
  }

  return (
    <view.div data-testid="conversations-widget-pane" uses={styles}>
      {children}
    </view.div>
  );
}

const universalStyles = css({
  ':global body': {
    // Since we iframe the widget we must ensure there's no body margin or it'll
    // create a weird gap inside the iframe.
    margin: css.value(0),
  },
});

const iframeStyles = css({
  ':global html': {
    height: css.value('100vh'),
    overflow: css.value('hidden'),
    position: css.value('relative'),
  },
});

export type Props = {
  children: React.ReactNode;
  defaultTitle?:
    | string
    | MessageDescriptor
    | React.ReactNode
    | undefined
    | null;
  header?: React.ReactNode;
  showHeader?: boolean;
};

export function Widget({
  children,
  defaultTitle,
  header = <Header defaultTitle={defaultTitle} />,
  showHeader = true,
}: Props) {
  const useDotComDesign = useUniversalHelpDotComDesign();
  const styles = [universalStyles];
  if (isIframed()) {
    styles.push(iframeStyles);
  }

  return (
    <view.div
      uses={styles}
      data-testid="conversations-widget-root"
      onMouseEnter={() => {
        // TODO: this is a work around for the Tooltip to show up immediately
        // It helps the modality as "pointer" (as oppose to "keyboard"), and tooltip is only shown if the user is using pointer
        document.dispatchEvent(new MouseEvent('pointerdown'));
        document.dispatchEvent(new MouseEvent('mousedown'));
      }}
      css={{
        background: 'surface',
        stack: 'y',
        height: 'fill',
        overflow: css.value('hidden'),
        position: css.value('relative'),
        ...(useDotComDesign
          ? {
              borderTopLeftRadius: 'medium',
              borderTopRightRadius: 'medium',
            }
          : {
              borderRadius: 'medium',
            }),
      }}
    >
      {showHeader ? header : null}
      <Pane>{children}</Pane>
    </view.div>
  );
}
